import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/index.js'
import contact2accessGroup2portfolioService from '@/services/contact2access-group2portfolio.service'

Vue.use(VueRouter)

// Helper to emit page navigation events
function emitPageEvent(eventName, detail = {}) {
  window.dispatchEvent(new CustomEvent(eventName, { detail }))
}

// Authentication guard with event emission
const checkAuthentication = (to, from, next) => {
  if (store.getters.isAuthenticated) {
    if (store.getters.isActivePortfolioBlocked && !store.getters.getCurrentContact.is_owner) {
      next('account')
      return
    }
    emitPageEvent(`vue-page-${to.name}`, { to, from })
    next()
    return
  }
  store.dispatch('autoLogin').then(response => {
    if (response) {
      emitPageEvent(`vue-page-${to.name}`, { to, from })
      next()
      return
    }
    next('/login')
  })
}

// Special guard for article details (example)
const fetchPortfolioByArticle = async (to, from, next) => {
  if (!from.name) {
    store.dispatch('autoLogin').then(async response => {
      if (response) {
        const articleId = to.params.id
        const portfolio = store.getters.getPortfolioByArticleId(articleId)
        if (portfolio) {
          const portfolioToContact = await contact2accessGroup2portfolioService.getOneWherePortfolioIdAndUserId(portfolio.id, response.id)
          if (!portfolioToContact.data || portfolioToContact.data.length === 0) {
            next('/error-403')
            return
          }
          const portfolioId = portfolio.id
          if (!store.getters.getActivePortfolio.id) {
            await store.dispatch("setActivePortfolio", portfolioId)
          }
        }
        emitPageEvent(`vue-page-article-details`, { to, from, articleId })
        next()
        return
      }
      next('/login')
    })
  } else {
    emitPageEvent(`vue-page-article-details`, { to, from, articleId: to.params.id })
    next()
  }
}

// Logout guard with event
const logout = (to, from, next) => {
  const user = localStorage.getItem("user")
  if (user) {
    store.dispatch("logout")
  }
  emitPageEvent(`vue-page-${to.name}`, { to, from })
  next()
}

const router = new VueRouter({
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Account.vue'),
      meta: { pageTitle: 'Hem', breadcrumb: [{ text: 'Hem', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/nyheter',
      name: 'news-feed',
      component: () => import('@/views/articles/NewsSearchResult.vue'),
      meta: { pageTitle: 'Nyheter', breadcrumb: [{ text: 'Nyheter', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/',
      name: 'calendar',
      component: () => import('@/views/apps/calendar/Calendar.vue'),
      meta: { pageTitle: 'Kalender', breadcrumb: [{ text: 'Kalender', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/test',
      name: 'test',
      component: () => import('@/views/Home.vue'),
      meta: { pageTitle: 'Home', breadcrumb: [{ text: 'Home', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/event:id/:alias',
      name: 'event-details',
      component: () => import('@/views/articles/EventArticleDetails.vue'),
      props: true,
      meta: { pageTitle: 'Kalender', breadcrumb: [{ text: 'Kalender', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/presentation',
      name: 'about-me',
      component: () => import('@/views/AboutMe.vue'),
      props: true,
      meta: { pageTitle: 'Min presentation' },
      beforeEnter: checkAuthentication
    },
    {
      path: '/min-information',
      name: 'search-articles',
      component: () => import('@/views/articles/ArticleSearch.vue'),
      meta: { pageTitle: 'Min information', breadcrumb: [{ text: 'Min pärm (alternativ)', active: false }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/kommunikationspass',
      name: 'communication-articles',
      component: () => import('@/views/articles/CommunicationSearch.vue'),
      meta: { pageTitle: 'Kommunikationspass', breadcrumb: [{ text: 'Min pärm (alternativ)', active: false }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/Viktigt',
      name: 'information-articles',
      component: () => import('@/views/articles/InformationSearch.vue'),
      meta: { pageTitle: 'Viktig information', breadcrumb: [{ text: 'Min pärm (alternativ)', active: false }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/resultat/:id/:alias',
      name: 'articles-in-category',
      component: () => import('@/views/articles/CategorySearchResult.vue'),
      props: true,
      meta: { pageTitle: 'Min information' },
      beforeEnter: checkAuthentication
    },
    {
      path: '/artikel/:id/:alias',
      name: 'article-details',
      component: () => import('@/views/articles/ArticleDetails.vue'),
      props: true,
      meta: { pageTitle: 'Visar artikel', breadcrumb: [{ text: 'Min pärm', active: false, to: { name: 'search-articles' } }] },
      beforeEnter: fetchPortfolioByArticle
    },
    {
      path: '/redigera-artikel',
      name: 'article-add',
      component: () => import('@/views/articles/ArticleAdd.vue'),
      props: true,
      meta: { pageTitle: 'Redigera artikel', breadcrumb: [{ text: 'Min pärm', active: false, to: { name: 'search-articles' } }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/kontakter',
      name: 'contacts',
      component: () => import('@/views/Contacts.vue'),
      meta: { pageTitle: 'Kontakter', breadcrumb: [{ text: 'Kontakter', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/kontakter/kontakt-kort/:id',
      name: 'contact-details',
      component: () => import('@/views/ContactDetails.vue'),
      props: true,
      meta: { pageTitle: 'Visar kontakt', breadcrumb: [{ text: 'Kontakter', active: true }] },
      beforeEnter: checkAuthentication
    },
    {
      path: '/kontakter/ny-kontakt',
      name: 'contact-add',
      component: () => import('@/views/ContactAdd.vue'),
      props: true,
      meta: { pageTitle: 'Lägg till kontakt', breadcrumb: [{ text: 'Kontakter', active: true }] },
      beforeEnter: (to, from, next) => {
        emitPageEvent(`vue-page-contact-add`, { to, from })
        next()
      }
    },
    {
      path: '/mitt-konto',
      name: 'account',
      component: () => import('@/views/Account.vue'),
      meta: { pageTitle: 'Mitt konto', breadcrumb: [{ text: 'Mitt konto', active: true }] },
      beforeEnter: checkAuthentication
    },
{
      path: '/Installningar',
      name: 'settings',
      component: () => import('@/views/Settings.vue'),
      meta: { pageTitle: 'Inställningar', breadcrumb: [{ text: 'Inställningar', active: true }] },
      beforeEnter: checkAuthentication,
      children: [
        {
          path: '/authorization',
          name: 'authorization',
          component: () => import('@/views/Authorization.vue'),
          meta: { pageTitle: 'Inställningar', breadcrumb: [{ text: 'Inställningar', active: true }] },
          beforeEnter: (to, from, next) => {
            emitPageEvent('vue-page-authorization', { to, from })
            checkAuthentication(to, from, next)
          }
        },
        {
          path: '/mediabibliotek',
          name: 'media-library',
          component: () => import('@/views/MediaLibrary.vue'),
          meta: { pageTitle: 'Mediabibliotek', breadcrumb: [{ text: 'Mediabibliotek', active: true }] },
          beforeEnter: (to, from, next) => {
            emitPageEvent('vue-page-media-library', { to, from })
            checkAuthentication(to, from, next)
          }
        }
      ]
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/login/:code',
      name: 'loginWithCode',
      component: () => import('@/views/Login.vue'),
      props: true,
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/Register.vue'),
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/register/:code',
      name: 'registerWithCode',
      component: () => import('@/views/Register.vue'),
      props: true,
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/email-verification/:code',
      name: 'email-verification',
      component: () => import('@/views/EmailVerification.vue'),
      props: true,
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPassword.vue'),
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/reset-password/:resetToken',
      name: 'reset-password',
      component: () => import('@/views/ResetPassword.vue'),
      props: true,
      meta: { layout: 'full' },
      beforeEnter: logout
    },
    {
      path: '/error-403',
      name: 'error-403',
      component: () => import('@/views/error/Error403.vue'),
      meta: { layout: 'full' },
      beforeEnter: (to, from, next) => {
        emitPageEvent('vue-page-error-403', { to, from })
        next()
      }
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: { layout: 'full' },
      beforeEnter: (to, from, next) => {
        emitPageEvent('vue-page-error-404', { to, from })
        next()
      }
    },
    {
      path: '*',
      redirect: 'error-404'
    },
    {
      path: '/permissions-calculator',
      name: 'permissions-calculator',
      component: () => import('@/views/permissions-calculator.vue'),
      meta: { pageTitle: 'Behörighets räknare', breadcrumb: [{ text: 'Behörighets räknare', active: true }] },
      beforeEnter: (to, from, next) => {
        emitPageEvent('vue-page-permissions-calculator', { to, from })
        checkAuthentication(to, from, next)
      }
    }
  ]
})

export default router